import { usePopper } from '@chakra-ui/popper';
import { Box, BoxProps, Flex, Icon, Text } from '@chakra-ui/react';
import { GetIngestionDocumentFragment } from '@client/graphql/__generated__/types';
import { computeOverlappingIndices } from '@common/extension';
import { getContractDocumentPageRange } from '@common/utils/validation';
import { CheckIcon, TriangleAlertIcon } from 'lucide-react';
import {
  createContext,
  Dispatch,
  FC,
  MouseEvent,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { match } from 'ts-pattern';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { PageFormFields } from '~/services/document/components/DocumentFormFields/PageFormFields';
import { FormField } from '~/services/document/components/FormFields/types';
import { PDFScrollableContext } from '~/services/document/components/PDFScrollable/context';
import { PDFScrollable } from '~/services/document/components/PDFScrollable/PDFScrollable';
import { PageMetadata } from '~/services/document/components/PDFScrollable/types';
import {
  calloutsCollide,
  pickMessageFieldIdsForPage,
  selectionCalloutAnchor,
  type CalloutAnchor,
} from './utils/fieldHighlights.utils';

interface CompliancePDFViewerProps {
  activeFieldId: string | string[];
  selectedDocumentLoading: boolean;
  selectedContractDocumentId: string | null;
  selectedDocument: GetIngestionDocumentFragment | null;
  /** Uploaded-PDF page explicitly selected by an embedding host. */
  selectedPageNumber?: number;
  isInternal: boolean;
  validationFieldsMap: Map<
    number,
    Array<{
      id: string;
      x: number;
      y: number;
      width: number;
      height: number;
      pageNumber: number;
      contractDocumentUuid: string;
    }>
  >;
  /**
   * When false, the viewer's own header (document name above the PDF) is
   * hidden. Useful when the embedding chrome already shows the doc name —
   * e.g. the SkySlope drawer renders the title in its own header bar.
   * Defaults to true to preserve the in-app drawer's existing layout.
   */
  showDocumentName?: boolean;
  /**
   * Field IDs known to have validation errors. When provided (alongside
   * {@link successFieldIds}) the viewer renders an overlay for every
   * classified field on each page — red for errors, green for successes —
   * instead of the legacy "only render activeFieldId" behaviour. The
   * `activeFieldId` field gets an additional outline so the user can still
   * tell which one they clicked.
   *
   * Used by the embedded SkySlope drawer, where the side panel pre-computes
   * the classification from the validation report and pushes it down via
   * host data attributes. Omit both to keep the legacy behaviour.
   */
  errorFieldIds?: string[];
  /** See {@link errorFieldIds}. */
  successFieldIds?: string[];
  /**
   * Reads a message's first newline as "rule text, then suggested value" and
   * draws the second half as a `SUGGESTED:` line. Off by default: only Smart
   * Assist encodes that; elsewhere a newline is just part of the message.
   */
  splitSuggestionOnNewline?: boolean;
  /** Frames each PDF page with a thin border. Off by default. */
  showPageBorders?: boolean;
  /**
   * Widest a page draws, in px; narrower containers fit the page to them.
   * Defaults to the window's width.
   */
  maxPageWidth?: number;
  /** Space above the first page and below the last, inside the scroll. */
  pagesPaddingY?: string;
  /** The side gutters either side of the pages. Defaults to `4`. */
  paddingX?: BoxProps['px'];
  /**
   * Lets a long field message wrap inside its callout, instead of the default
   * one line cut short with an ellipsis.
   */
  wrapFieldMessages?: boolean;
  /**
   * While a field is selected, hides every other field's callout and fades
   * its highlight, so the selection stands out. Hovering or tapping one of
   * those still shows its message, in full. The selection's callout points
   * past the selected fields near it, so it never covers them.
   */
  focusSelectedFields?: boolean;
  /**
   * Validation message text to display in a callout anchored to the active
   * field (e.g. "Missing: Date"). Renders only when `activeFieldId` matches
   * one of the classified fields. Omit to suppress the callout.
   *
   * Used as the fallback when `errorFieldMessages` doesn't carry a per-field
   * message for the active field.
   */
  activeFieldMessage?: string;
  /**
   * Per-field error message map keyed by field id (e.g.
   * `{ "field-1": "Missing: Date" }`). When provided, every error field
   * with a message renders its own Popper-positioned tooltip — matching
   * the platform-page overlays' "tooltip per field" behaviour in
   * `extension/content-scripts/components/common/ValidationFormFields.tsx`.
   * Omit for the legacy "callout only on active" behaviour.
   */
  errorFieldMessages?: Record<string, string>;
  /**
   * Field IDs to render as warnings (yellow). Purely additive: callers that
   * omit this keep the existing error (red) / success (green) behaviour
   * unchanged. When provided, these fields participate in classification mode
   * alongside {@link errorFieldIds} / {@link successFieldIds} and are painted
   * yellow.
   *
   * Color precedence per field is error (red) > warning (yellow) > success
   * (green): a field that is both an error and a warning renders as an error.
   *
   * Used by the agent compliance review page, which surfaces failed-validation
   * / suggestion fields as warnings (no green for passing fields).
   */
  warningFieldIds?: string[];
  /**
   * Per-field warning message map keyed by field id (e.g.
   * `{ "field-1": "Suggested: add closing date" }`). Mirrors
   * {@link errorFieldMessages}: every warning field with a message renders its
   * own Popper-positioned tooltip. Omit for no warning tooltips.
   */
  warningFieldMessages?: Record<string, string>;
  /**
   * How many callouts to paint when several fields on a page carry the SAME
   * message.
   *
   * - `'all'` (default): every field with a message paints its own callout —
   *   the Quick-Audit-matching behaviour the drawer and platform overlays use.
   * - `'firstPerPage'`: only the first field per page per distinct message
   *   paints one (reading order); the rest keep their highlight and reveal
   *   their message on hover/tap. Used by the agent compliance review page,
   *   where a rule matching a field per paragraph otherwise buries the
   *   document under stacked callouts (BLO-13495).
   * - `'none'`: no callouts up front — highlights only, every message behind a
   *   hover/tap. What the agent page's hide-all toggle switches to when the
   *   reviewer wants an unobstructed read of the document.
   */
  fieldMessageDisplay?: 'all' | 'firstPerPage' | 'none';
  /**
   * Measures the final Popper callouts and hides any that overlap another
   * callout or highlighted field. Hidden messages remain available on
   * hover/tap. Off by default; embedded document drawers opt in explicitly.
   */
  hideOverlappingFieldMessages?: boolean;
  /** Called when pdf.js cannot load the selected PDF URL. */
  onPdfLoadError?: (error: Error) => void;
  /**
   * Background color token for the viewer's outer container — the "desk" the
   * PDF page sits on, including the horizontal padding gutters on either side.
   * Defaults to `compliance.pdf.bg`. The embedded SkySlope drawer overrides
   * this with the selected-tab background so the viewer's side gutters blend
   * into the drawer chrome instead of reading as a separate darker band.
   */
  containerBg?: string;
  /** Overrides the legacy contract-file-first URL selection. */
  preferredPdfUrl?: string;
  /**
   * Maps the chosen PDF URL to a fetchable one, after the usual
   * contract-file-first selection has run.
   *
   * Omit it and the selected URL is used as-is, synchronously — the path every
   * surface but the SkySlope embedded widget takes. That widget supplies one
   * because it renders on SkySlope's origin, where the root-relative `/files/…`
   * URL the API returns would resolve against the wrong host, and because the
   * URL needs an authenticated exchange before it can be fetched anonymously.
   *
   * May be async; the viewer shows its loading state until it settles. Give it
   * a stable identity (`useCallback`) or it will re-resolve on every render.
   * Applied to the result rather than to each candidate so the selection order
   * stays in one place.
   */
  resolveFileUrl?: (url: string) => string | Promise<string>;
}

/**
 * The one field whose message is currently shown by hover or tap, if any.
 *
 * Lives in context rather than in each {@link FieldHighlight} because revealing
 * a message has to suppress the page's default callout for that SAME message,
 * and any it would land on — cross-field coordination the individual
 * highlights can't do alone. Context
 * (rather than a prop threaded through `pageChildren`) keeps that page component's
 * identity stable: it is a `useCallback` value, and re-creating it on every hover
 * would remount every field rect under the cursor mid-hover.
 */
interface RevealedFieldMessage {
  fieldId: string;
  /**
   * The box its callout is drawn beside (the field's anchor when it has one),
   * so the default callouts it would cover can step aside.
   */
  field: { x: number; y: number; width: number; height: number };
  pageNumber: number;
  message: string;
  /** Pinned by a click/tap; survives the pointer leaving. */
  pinned: boolean;
}

const RevealedFieldMessageContext = createContext<{
  revealed: RevealedFieldMessage | null;
  setRevealed: Dispatch<SetStateAction<RevealedFieldMessage | null>>;
}>({ revealed: null, setRevealed: () => {} });

interface FieldElementRegistration {
  box: HTMLElement | null;
  tooltip: HTMLElement | null;
  uploadedPageNumber: number;
}

type RegisterFieldElement = (
  fieldId: string,
  type: 'box' | 'tooltip',
  element: HTMLElement | null,
  uploadedPageNumber: number
) => void;

const setsEqual = <T,>(left: Set<T>, right: Set<T>): boolean =>
  left.size === right.size && [...left].every((value) => right.has(value));

/**
 * Renders one classified field's rectangle overlay, plus a Popper-positioned
 * tooltip when this field has a message.
 *
 * Every error field with a message renders its own tooltip — not only the
 * currently-active field. The active field is distinguished by a thicker
 * outline in the status color (set via `isActive` + `activeBorderColor`).
 * Mirrors the platform-page overlay pattern in
 * `extension/content-scripts/components/common/ValidationFormFields.tsx`,
 * where each `<SingleField>` independently renders its own Popper'd
 * tooltip with the parent error's message.
 *
 * We extract this because `usePopper` is a hook — it has to be called per
 * rendered field at top level, not inside a map. Splitting it out also
 * keeps the tooltip as a sibling of the rect (instead of a child),
 * which lets Popper position it freely relative to the viewport bounds
 * instead of being clipped by the rect's tiny absolute-positioned box.
 *
 * Placement is `'right'` by default; Popper's `flip` + `preventOverflow`
 * modifiers auto-rotate to `left` / `top` / `bottom` if the tooltip would
 * spill outside its boundary.
 */
interface FieldHighlightProps {
  field: {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
  };
  page: PageMetadata;
  isFieldError: boolean;
  /**
   * Warning fields get the SAME callout/tooltip treatment as errors (they
   * carry messages) but render the alert icon in yellow rather than red.
   * Mutually exclusive with `isFieldError` per the caller's precedence
   * (error > warning). Defaults to false so existing error/success callers
   * are unaffected.
   */
  isFieldWarning?: boolean;
  isActive: boolean;
  bgToken: string;
  activeBorderColor: string;
  /** Message for THIS field, if any. Renders the tooltip when present. */
  message?: string;
  /**
   * Whether this field is one of the page's chosen callouts. False means it was
   * thinned out by `fieldMessageDisplay: 'firstPerPage'` because another field on
   * the page already says the same thing: it keeps its highlight, and its message
   * appears only while hovered (mouse) or tapped, so nothing is unreachable.
   *
   * Defaults to true — every messaged field speaks — which is the `'all'`
   * behaviour the drawer and platform overlays rely on.
   */
  isDefaultCaptioned?: boolean;
  /** See {@link CompliancePDFViewerProps.splitSuggestionOnNewline}. */
  splitSuggestionOnNewline?: boolean;
  /** See {@link CompliancePDFViewerProps.wrapFieldMessages}. */
  wrapFieldMessages?: boolean;
  /** Another field is selected; see `focusSelectedFields`. */
  isDimmed?: boolean;
  /**
   * The box the callout points at, when not the field's own: see
   * `selectionCalloutAnchor`. In the page's normalized coordinates.
   */
  calloutAnchor?: CalloutAnchor;
  collisionMeasurementComplete: boolean;
  hideOverlappingFieldMessages: boolean;
  isCollisionHidden: boolean;
  registerFieldElement: RegisterFieldElement;
  scheduleOverlapMeasurement: () => void;
}

/** How far `focusSelectedFields` fades the fields that are not selected. */
const DIMMED_OPACITY = 0.35;

const FieldHighlight: FC<FieldHighlightProps> = ({
  field,
  page,
  isFieldError,
  isFieldWarning = false,
  isActive,
  bgToken,
  activeBorderColor,
  message,
  isDefaultCaptioned = true,
  splitSuggestionOnNewline = false,
  wrapFieldMessages = false,
  isDimmed = false,
  calloutAnchor,
  collisionMeasurementComplete,
  hideOverlappingFieldMessages,
  isCollisionHidden,
  registerFieldElement,
  scheduleOverlapMeasurement,
}) => {
  const { revealed, setRevealed } = useContext(RevealedFieldMessageContext);
  // Only a thinned-out field needs an interaction to speak. The captioned ones
  // stay inert so they never swallow a drag-to-pan on the PDF underneath.
  const isInteractive = !!message && (!isDefaultCaptioned || isCollisionHidden);
  const isRevealed = revealed?.fieldId === field.id;
  const opacity = isDimmed && !isRevealed ? DIMMED_OPACITY : undefined;
  // A revealed field REPLACES the page's default callout for the same message
  // rather than adding a second identical bubble beside it, and hides one it
  // would land on so the two are never stacked. Scoped to this page, so an
  // equally-worded issue elsewhere in the document keeps its own callout.
  const isSupersededByReveal =
    isDefaultCaptioned &&
    message != null &&
    revealed != null &&
    !isRevealed &&
    revealed.pageNumber === page.pageNumber &&
    (revealed.message === message ||
      calloutsCollide(
        { field: revealed.field, message: revealed.message },
        // Where this callout is drawn: beside its anchor when it has one.
        { field: calloutAnchor ?? field, message },
        page,
        { splitSuggestion: splitSuggestionOnNewline, wrap: wrapFieldMessages }
      ));

  const reveal = useCallback(
    (pinned: boolean) => {
      if (!message) {
        return;
      }
      setRevealed((previous) =>
        // Hovering never downgrades this field's own pin to transient; it does
        // replace another field's, so at most one extra callout is ever up.
        !pinned && previous?.fieldId === field.id && previous.pinned
          ? previous
          : {
              field: calloutAnchor ?? field,
              fieldId: field.id,
              pageNumber: page.pageNumber,
              message,
              pinned,
            }
      );
    },
    [calloutAnchor, field, message, page.pageNumber, setRevealed]
  );
  // Errors and warnings both carry per-field messages and get the alert-icon
  // callout treatment; successes get the green check. Error and warning are
  // mutually exclusive (error wins) per the caller's precedence.
  const hasStatusCallout = isFieldError || isFieldWarning;
  const showCallout =
    !!message && (isRevealed || (isDefaultCaptioned && !isSupersededByReveal));
  const isCalloutVisible =
    showCallout &&
    (!hideOverlappingFieldMessages ||
      isRevealed ||
      (collisionMeasurementComplete && !isCollisionHidden));
  const shouldMountCallout = showCallout;
  // Split once, at the first newline, so a suggestion may hold newlines too.
  const separatorIndex =
    splitSuggestionOnNewline && message != null ? message.indexOf('\n') : -1;
  const messageText =
    separatorIndex === -1 ? message : message?.slice(0, separatorIndex);
  const suggestionText =
    separatorIndex === -1 ? undefined : message?.slice(separatorIndex + 1);
  const hasSuggestion = suggestionText != null;
  const { calloutLayer, zoom } = useContext(PDFScrollableContext);
  const {
    referenceRef,
    getPopperProps,
    getReferenceProps,
    getArrowProps,
    getArrowInnerProps,
    forceUpdate,
  } = usePopper({
    placement: 'right',
    gutter: 6,
    // `flip` + `preventOverflow` are on by default; listed here for clarity.
    flip: true,
    preventOverflow: true,
    // popper.js's default `fallbackPlacements` for `right` is just
    // `['left']`. For a full-page-width field, neither right nor left has
    // room, so without explicit top/bottom fallbacks popper just sticks
    // with `right` and the tooltip ends up clipped off the page. The
    // override re-adds the perpendicular axis so wide fields get a
    // tooltip above or below them instead.
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left', 'top', 'bottom'],
        },
      },
    ],
  });

  const setFieldElement = useCallback(
    (element: HTMLDivElement | null) =>
      registerFieldElement(field.id, 'box', element, page.pageNumber),
    [field.id, page.pageNumber, registerFieldElement]
  );
  const setTooltipElement = useCallback(
    (element: HTMLDivElement | null) =>
      registerFieldElement(field.id, 'tooltip', element, page.pageNumber),
    [field.id, page.pageNumber, registerFieldElement]
  );

  // Chakra hands back a new `forceUpdate` every render; the ref keeps the
  // effects below to the changes they are about.
  const forceUpdateRef = useRef(forceUpdate);

  forceUpdateRef.current = forceUpdate;

  // Popper re-measures only on scroll and window resize. A zoom, and pages
  // streaming in or redrawing around the field, move it without either.
  useEffect(() => {
    forceUpdateRef.current();
    scheduleOverlapMeasurement();
  }, [scheduleOverlapMeasurement, zoom]);

  useEffect(() => {
    if (
      !shouldMountCallout ||
      calloutLayer == null ||
      typeof ResizeObserver === 'undefined'
    ) {
      return;
    }

    const observer = new ResizeObserver(() => {
      forceUpdateRef.current();
      scheduleOverlapMeasurement();
    });

    observer.observe(calloutLayer);

    return () => observer.disconnect();
  }, [calloutLayer, scheduleOverlapMeasurement, shouldMountCallout]);

  // In the scroll area's unscaled layer when there is one (see
  // `calloutLayer`), in place otherwise.
  const inCalloutLayer = (callout: JSX.Element): ReactNode =>
    calloutLayer == null ? callout : createPortal(callout, calloutLayer);

  return (
    <>
      {calloutAnchor != null && (
        <Box
          ref={referenceRef}
          pointerEvents="none"
          position="absolute"
          style={{
            height: `${calloutAnchor.height * page.height}px`,
            left: `${calloutAnchor.x * page.width}px`,
            top: `${calloutAnchor.y * page.height}px`,
            width: `${calloutAnchor.width * page.width}px`,
          }}
        />
      )}
      <Box
        {...(calloutAnchor == null
          ? getReferenceProps({}, setFieldElement)
          : { ref: setFieldElement })}
        bgColor={bgToken}
        borderColor={isActive ? activeBorderColor : undefined}
        // Chakra doesn't imply `border-style: solid` from width + color
        // alone — explicit `borderStyle` is required or the browser
        // defaults to `none` and the active outline is invisible.
        borderStyle={isActive ? 'solid' : undefined}
        borderWidth={isActive ? '2px' : '0px'}
        cursor={isInteractive ? 'pointer' : undefined}
        data-preview-field-id={field.id}
        data-preview-page-number={page.pageNumber}
        // Highlights are inert by default so they never swallow a drag-to-pan or
        // text selection on the PDF underneath. Only a field whose message is
        // hidden takes pointer events, and only to reveal it — mousedown still
        // bubbles, so panning from on top of one keeps working.
        opacity={opacity}
        pointerEvents={isInteractive ? 'auto' : 'none'}
        position="absolute"
        style={{
          height: `${field.height * page.height}px`,
          left: `${field.x * page.width}px`,
          top: `${field.y * page.height}px`,
          width: `${field.width * page.width}px`,
        }}
        // Click/tap pins, so the message stays readable without holding a
        // pointer on it — the only way in on a touch device, and the way out is
        // the same tap again.
        onClick={
          isInteractive
            ? () => {
                setRevealed((previous) =>
                  previous?.fieldId === field.id && previous.pinned
                    ? null
                    : {
                        field: calloutAnchor ?? field,
                        fieldId: field.id,
                        pageNumber: page.pageNumber,
                        message: message ?? '',
                        pinned: true,
                      }
                );
              }
            : undefined
        }
        // Hover is gated on a real mouse. Touch browsers synthesize a
        // pointerover on tap and frequently never fire the matching pointerout,
        // which would leave a phone's callout undismissable — the pointerType
        // guard keeps touch on the pin path alone.
        onPointerEnter={
          isInteractive
            ? (event) => {
                if (event.pointerType === 'mouse') {
                  reveal(false);
                }
              }
            : undefined
        }
        onPointerLeave={
          isInteractive
            ? (event) => {
                if (event.pointerType !== 'mouse') {
                  return;
                }
                // Leave clears only a transient hover; a pinned callout stays.
                setRevealed((previous) =>
                  previous?.fieldId === field.id && !previous.pinned
                    ? null
                    : previous
                );
              }
            : undefined
        }
      />
      {shouldMountCallout &&
        inCalloutLayer(
          <Box
            {...getPopperProps({}, setTooltipElement)}
            data-field-message-for={field.id}
            // The layer takes no pointer events; the callout still does.
            opacity={
              hideOverlappingFieldMessages
                ? isCalloutVisible
                  ? 1
                  : 0
                : undefined
            }
            pointerEvents={isCalloutVisible ? 'auto' : 'none'}
            // Above the field rects. They are absolutely positioned with no
            // z-index of their own, so a rect later in the DOM paints over an
            // earlier field's callout — which is every callout whose field is
            // not the last one on the page. This has to sit on the popper
            // container: it is the positioned element, and `z-index` on the
            // static bubble inside it is ignored.
            zIndex={1}
            // `--popper-arrow-size` MUST live on the popper container: Chakra
            // sizes the arrow positioner ([data-popper-arrow]) from this var, and
            // Popper measures that element to vertically center the arrow on the
            // field. Setting the size via `getArrowProps({ size })` instead left
            // the positioner unsized, so the arrow rendered off-center. The fill
            // (`bg`) does work through `getArrowProps`, so that stays below.
          >
            {/* Arrow: Popper positions this on the side opposite the chosen
              placement (right placement → arrow on the tooltip's left edge,
              etc.) so it always points at the field. The inner element is the
              visible rotated square; its fill comes from the `bg` passed to
              getArrowProps (→ `--popper-arrow-bg`). */}
            <Box
              {...getArrowProps({
                size: '8px',
                bg: 'var(--chakra-colors-indigo-900)',
              })}
            >
              <Box {...getArrowInnerProps()} />
            </Box>
            <Box
              alignItems={
                hasSuggestion || wrapFieldMessages ? 'flex-start' : 'center'
              }
              bg="indigo.900"
              boxShadow="0 0 7px rgba(0, 0, 0, 0.25)"
              color="white"
              display="inline-flex"
              lineHeight={1}
              maxWidth="240px"
              // Wrapped text keeps the icon level with its first line. Only the
              // two-part body takes the roomier spacing; every other callout
              // keeps the compact one.
              width="max-content"
              {...(hasSuggestion
                ? { borderRadius: '4px', gap: 3, p: 3 }
                : { borderRadius: 'xs', gap: 1, px: 1.5, py: 1 })}
            >
              <Icon
                as={hasStatusCallout ? TriangleAlertIcon : CheckIcon}
                boxSize="12px"
                color={
                  isFieldError
                    ? 'red.500'
                    : isFieldWarning
                      ? 'yellow.500'
                      : 'green.500'
                }
                flexShrink={0}
              />
              {!hasSuggestion ? (
                <Text
                  as="span"
                  color="white"
                  fontSize="xs"
                  fontWeight="medium"
                  {...(wrapFieldMessages
                    ? { lineHeight: 1.3 }
                    : {
                        lineHeight: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      })}
                >
                  {messageText}
                </Text>
              ) : (
                <Box>
                  <Text
                    color="white"
                    fontSize="sm"
                    fontWeight={500}
                    lineHeight={1.3}
                  >
                    {messageText}
                  </Text>
                  {suggestionText != null && (
                    <Flex align="baseline" gap={2} mt={1}>
                      <Text
                        color="#8A90A6"
                        fontSize="sm"
                        letterSpacing="0.09em"
                        textTransform="uppercase"
                      >
                        Suggested:
                      </Text>
                      <Text color="white" fontSize="sm">
                        {suggestionText}
                      </Text>
                    </Flex>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
    </>
  );
};

export const CompliancePDFViewer: FC<CompliancePDFViewerProps> = ({
  activeFieldId,
  selectedContractDocumentId,
  selectedDocument,
  selectedPageNumber: requestedPageNumber,
  selectedDocumentLoading,
  validationFieldsMap,
  isInternal = false,
  showDocumentName = true,
  errorFieldIds,
  successFieldIds,
  splitSuggestionOnNewline = false,
  showPageBorders = false,
  maxPageWidth,
  pagesPaddingY,
  paddingX = 4,
  wrapFieldMessages = false,
  focusSelectedFields = false,
  activeFieldMessage,
  errorFieldMessages,
  warningFieldIds,
  warningFieldMessages,
  fieldMessageDisplay = 'all',
  hideOverlappingFieldMessages = false,
  onPdfLoadError,
  containerBg = 'compliance.pdf.bg',
  preferredPdfUrl,
  resolveFileUrl,
}) => {
  const hasClassifications = !!(
    errorFieldIds ||
    successFieldIds ||
    warningFieldIds
  );
  const errorIdSet = useMemo(
    () => new Set(errorFieldIds ?? []),
    [errorFieldIds]
  );
  const successIdSet = useMemo(
    () => new Set(successFieldIds ?? []),
    [successFieldIds]
  );
  const warningIdSet = useMemo(
    () => new Set(warningFieldIds ?? []),
    [warningFieldIds]
  );
  // The single hover/tap-revealed message, shared with every FieldHighlight so a
  // reveal can supersede the page's default callout for the same text.
  const [revealed, setRevealed] = useState<RevealedFieldMessage | null>(null);
  const revealedFieldMessage = useMemo(
    () => ({ revealed, setRevealed }),
    [revealed]
  );
  const fieldElementRegistrations = useRef<
    Map<string, FieldElementRegistration>
  >(new Map());
  const overlapMeasurementFrames = useRef<number[]>([]);
  const [overlappingFieldIds, setOverlappingFieldIds] = useState<Set<string>>(
    new Set()
  );
  const [collisionMeasurementComplete, setCollisionMeasurementComplete] =
    useState(false);

  const cancelOverlapMeasurement = useCallback((): void => {
    overlapMeasurementFrames.current.forEach((frame) =>
      cancelAnimationFrame(frame)
    );
    overlapMeasurementFrames.current = [];
  }, []);

  const measureOverlaps = useCallback((): void => {
    const entries = [...fieldElementRegistrations.current.entries()]
      .filter(
        (
          entry
        ): entry is [string, FieldElementRegistration & { box: HTMLElement }] =>
          entry[1].box != null
      )
      .map(([id, registration]) => ({
        id,
        boxRect: registration.box.getBoundingClientRect(),
        tooltipRect: registration.tooltip?.getBoundingClientRect() ?? null,
        page: registration.uploadedPageNumber,
      }));
    const nextOverlappingFieldIds = computeOverlappingIndices(entries);

    setOverlappingFieldIds((previous) =>
      setsEqual(previous, nextOverlappingFieldIds)
        ? previous
        : nextOverlappingFieldIds
    );
    setCollisionMeasurementComplete(true);
  }, []);

  const scheduleOverlapMeasurement = useCallback((): void => {
    if (!hideOverlappingFieldMessages) return;

    cancelOverlapMeasurement();
    if (typeof requestAnimationFrame === 'undefined') {
      setCollisionMeasurementComplete(true);

      return;
    }

    const firstFrame = requestAnimationFrame(() => {
      const secondFrame = requestAnimationFrame(measureOverlaps);
      overlapMeasurementFrames.current = [secondFrame];
    });
    overlapMeasurementFrames.current = [firstFrame];
  }, [cancelOverlapMeasurement, hideOverlappingFieldMessages, measureOverlaps]);

  const registerFieldElement = useCallback<RegisterFieldElement>(
    (fieldId, type, element, uploadedPageNumber) => {
      const registration = fieldElementRegistrations.current.get(fieldId) ?? {
        box: null,
        tooltip: null,
        uploadedPageNumber,
      };

      registration[type] = element;
      registration.uploadedPageNumber = uploadedPageNumber;
      if (registration.box == null && registration.tooltip == null) {
        fieldElementRegistrations.current.delete(fieldId);
      } else {
        fieldElementRegistrations.current.set(fieldId, registration);
      }
      scheduleOverlapMeasurement();
    },
    [scheduleOverlapMeasurement]
  );

  useEffect(() => () => cancelOverlapMeasurement(), [cancelOverlapMeasurement]);
  // The document this viewer is currently showing. Both agent-review layouts keep
  // ONE viewer mounted while the agent switches document tabs, so this is what
  // tells us the content underneath changed.
  const selectedPdfUrl =
    preferredPdfUrl ||
    selectedDocument?.contractDocuments?.[0]?.file?.url ||
    selectedDocument?.fileUrl ||
    '';

  useEffect(() => {
    setOverlappingFieldIds((previous) =>
      previous.size === 0 ? previous : new Set()
    );
    setCollisionMeasurementComplete(!hideOverlappingFieldMessages);
    scheduleOverlapMeasurement();
  }, [
    errorFieldMessages,
    hideOverlappingFieldMessages,
    scheduleOverlapMeasurement,
    selectedPdfUrl,
    validationFieldsMap,
    warningFieldMessages,
  ]);
  const viewerRef = useRef<HTMLDivElement>(null);
  const selectedPageRange = getContractDocumentPageRange(
    selectedDocument?.contractDocumentPages,
    selectedContractDocumentId
  );
  const selectedPageNumber =
    requestedPageNumber ??
    selectedPageRange?.pages?.[0] ??
    selectedPageRange?.startPage;
  const hasActiveField = Array.isArray(activeFieldId)
    ? activeFieldId.length > 0
    : !!activeFieldId;

  // Only used when a `resolveFileUrl` is supplied. Without one the selected
  // URL is passed straight through below, so the common path keeps rendering
  // on the first frame instead of waiting a tick for state.
  const [asyncResolvedPdfUrl, setAsyncResolvedPdfUrl] = useState('');

  useEffect(() => {
    if (!resolveFileUrl || !selectedPdfUrl) {
      setAsyncResolvedPdfUrl('');

      return;
    }

    let isCancelled = false;
    // Clear first: showing the previous document's PDF while the next one
    // resolves would caption it with the new document's validation issues.
    setAsyncResolvedPdfUrl('');

    void Promise.resolve(resolveFileUrl(selectedPdfUrl)).then((resolved) => {
      if (isCancelled) return;
      setAsyncResolvedPdfUrl(resolved);
    });

    return () => {
      isCancelled = true;
    };
  }, [resolveFileUrl, selectedPdfUrl]);

  const pdfFileUrl = resolveFileUrl ? asyncResolvedPdfUrl : selectedPdfUrl;

  const [pageCounter, setPageCounter] = useState<{
    pdfUrl: string;
    pageNumber: number | null;
    totalPages: number | null;
  }>({ pdfUrl: '', pageNumber: null, totalPages: null });

  useEffect(() => {
    setPageCounter({
      pdfUrl: pdfFileUrl,
      pageNumber: null,
      totalPages: null,
    });
  }, [pdfFileUrl]);

  const handleVisiblePageChange = useCallback(
    ({ pageNumber }: { pageNumber: number }): void => {
      setPageCounter((previous) => ({
        pdfUrl: pdfFileUrl,
        pageNumber,
        totalPages: previous.pdfUrl === pdfFileUrl ? previous.totalPages : null,
      }));
    },
    [pdfFileUrl]
  );

  const handlePagesMetadataChange = useCallback(
    (pages: PageMetadata[]): void => {
      setPageCounter((previous) => ({
        pdfUrl: pdfFileUrl,
        pageNumber: previous.pdfUrl === pdfFileUrl ? previous.pageNumber : null,
        // These are pages in the displayed PDF, not the classified form's
        // canonical pages or a potentially stale ingestion page count.
        totalPages: pages.length,
      }));
    },
    [pdfFileUrl]
  );

  const showPageCounter =
    isInternal &&
    pageCounter.pdfUrl === pdfFileUrl &&
    pageCounter.pageNumber != null &&
    pageCounter.totalPages != null &&
    pageCounter.pageNumber > 0 &&
    pageCounter.pageNumber <= pageCounter.totalPages;

  // Pages mount after the PDF fetch. Observe this viewer until the selected
  // uploaded page exists, including when the selection changes within one PDF.
  // An active issue has its own field-level scroll and takes precedence.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (
      !viewer ||
      !pdfFileUrl ||
      selectedDocumentLoading ||
      hasActiveField ||
      selectedPageNumber == null
    ) {
      return;
    }
    const scrollToSelectedPage = (): boolean => {
      const page = viewer.querySelector<HTMLElement>(
        `div[data-page-number="${selectedPageNumber}"]`
      );
      if (!page) return false;
      page.scrollIntoView({ block: 'start' });

      return true;
    };
    if (scrollToSelectedPage()) return;
    const observer = new MutationObserver(() => {
      if (scrollToSelectedPage()) observer.disconnect();
    });
    observer.observe(viewer, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [
    selectedDocument?.id,
    selectedDocumentLoading,
    hasActiveField,
    selectedContractDocumentId,
    selectedPageNumber,
    pdfFileUrl,
  ]);

  // Drop any revealed/pinned callout when the thing it described goes away:
  //  - a new document: a pin from document A must not survive into B. Suppression
  //    matches on page number + message text, so a stale pin would silently
  //    swallow B's own callout for an identically-worded issue on the same page —
  //    while its field id doesn't exist in B, leaving that message uncaptioned.
  //  - new validation data (an inline ignore/undo re-derives the field map).
  //  - a density change: "hide all" while one is pinned should leave a clean
  //    document, not the one bubble the toggle can't reach.
  //  - a new selection: a pin from before would hide the selected issue's
  //    callout wherever the two collide. Keyed on the ids, not the array, so a
  //    caller passing a fresh array each render does not clear a hover.
  const activeFieldKey = Array.isArray(activeFieldId)
    ? activeFieldId.join('\n')
    : activeFieldId;

  useEffect(
    () => setRevealed(null),
    [activeFieldKey, fieldMessageDisplay, pdfFileUrl, validationFieldsMap]
  );
  /**
   * Dismisses a pinned callout when the agent taps anything that is not itself a
   * message-revealing highlight — blank page, the PDF, or one of the inert
   * highlights (whose `pointer-events: none` sends the tap to the PDF beneath).
   * Without this, re-tapping the pinned box was the ONLY way out on a phone,
   * which reads as stuck.
   *
   * Capture phase, deliberately: it runs before anything inside the PDF subtree
   * can stop the click, so dismissal can't be swallowed. That means it also runs
   * before the tapped highlight's own toggle, hence the target check — otherwise
   * this would clear the pin a moment before that handler re-set it, and the
   * callout could never be dismissed by tapping the same box either.
   */
  const dismissPinnedRevealOnOutsideClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (
        event.target instanceof Element &&
        event.target.closest('[data-preview-field-id]') != null
      ) {
        return;
      }
      setRevealed((previous) => (previous?.pinned ? null : previous));
    },
    []
  );
  const ValidationFieldPageChildren = useCallback(
    ({ page }: { page: PageMetadata }) => {
      const fields = validationFieldsMap.get(page.pageNumber) || [];
      const repositionedFields =
        selectedContractDocumentId &&
        selectedDocument?.fields?.[selectedContractDocumentId]
          ? (selectedDocument.fields[
              selectedContractDocumentId
            ] as unknown as FormField[])
          : undefined;

      const activeFieldIdString = Array.isArray(activeFieldId)
        ? activeFieldId[0]
        : activeFieldId;

      const filteredRepositionedFields = repositionedFields?.filter((field) => {
        return (
          !activeFieldIdString ||
          field.id !== `${activeFieldIdString}-repositioned`
        );
      });

      // Classification mode (embedded drawer): render every error/success
      // field on this page in red/green; outline the active one in white
      // so it still reads as "the one you clicked." Scope to
      // `selectedContractDocumentId` when set, matching legacy mode — a
      // single page can carry fields from multiple contract documents and
      // we don't want to paint the wrong PDF's classifications.
      // Legacy mode (in-app drawer): keep the original behaviour — only
      // render the activeFieldId field, in the single blue.
      const visibleFields = hasClassifications
        ? fields.filter(({ id, contractDocumentUuid }) => {
            if (
              !errorIdSet.has(id) &&
              !successIdSet.has(id) &&
              !warningIdSet.has(id)
            )
              return false;
            if (selectedContractDocumentId) {
              return contractDocumentUuid === selectedContractDocumentId;
            }

            return true;
          })
        : fields.filter(({ id, contractDocumentUuid }) => {
            const fieldMatches = Array.isArray(activeFieldId)
              ? activeFieldId.includes(id)
              : activeFieldId === id;
            if (selectedContractDocumentId && fieldMatches) {
              return contractDocumentUuid === selectedContractDocumentId;
            }

            return fieldMatches;
          });

      // Classification mode treats EVERY id in `activeFieldId` as active, so a
      // validation issue spanning several fields outlines all of them as one
      // selection. Callers passing a single string (the SkySlope drawer) are
      // unaffected — the set just holds one id.
      const activeFieldIdSet = new Set(
        (Array.isArray(activeFieldId) ? activeFieldId : [activeFieldId]).filter(
          (id) => !!id
        )
      );

      // Resolve a field's callout text.
      // - Errors: prefer the per-field error message map (matches Quick Audit's
      //   "tooltip per field" behaviour), so every error field has its own
      //   message whether the user has clicked it or not.
      // - Warnings: same per-field treatment, from the warning message map.
      // - Successes: only the active (just-clicked) success has one, from
      //   `activeFieldMessage`. There's no bulk map for successes because we
      //   don't paint them by default.
      const messageForFieldId = (fieldId: string): string | undefined => {
        const isFieldError = errorIdSet.has(fieldId);
        const isFieldWarning = !isFieldError && warningIdSet.has(fieldId);

        if (isFieldError && errorFieldMessages?.[fieldId]) {
          return errorFieldMessages[fieldId];
        }
        if (isFieldWarning && warningFieldMessages?.[fieldId]) {
          return warningFieldMessages[fieldId];
        }
        if (
          hasClassifications &&
          activeFieldIdSet.has(fieldId) &&
          activeFieldMessage
        ) {
          return activeFieldMessage;
        }

        return undefined;
      };

      // Anywhere in the document, so the whole of it steps back.
      const hasFocus =
        focusSelectedFields && hasClassifications && activeFieldIdSet.size > 0;

      const calloutLayout = {
        splitSuggestion: splitSuggestionOnNewline,
        wrap: wrapFieldMessages,
      };

      const selectedOnPage = hasFocus
        ? visibleFields.filter((field) => activeFieldIdSet.has(field.id))
        : [];

      // Which of this page's fields paint their callout unprompted. `null` means
      // "all of them" — the default, unthinned behaviour.
      const calloutFieldIds = match(fieldMessageDisplay)
        .with('all', () => null)
        .with('firstPerPage', () =>
          pickMessageFieldIdsForPage(
            visibleFields,
            messageForFieldId,
            activeFieldIdSet,
            { height: page.height, width: page.width },
            calloutLayout
          )
        )
        .with('none', () => new Set<string>())
        .exhaustive();

      return (
        <>
          {isInternal && filteredRepositionedFields && (
            <PageFormFields
              formFields={filteredRepositionedFields}
              page={page}
              updateValues={() => {}}
              values={{}}
            />
          )}
          {visibleFields.map((field) => {
            // Color precedence per field: error (red) > warning (yellow) >
            // success (green). A field that is both an error and a warning
            // renders as an error.
            const isFieldError = errorIdSet.has(field.id);
            const isFieldWarning = !isFieldError && warningIdSet.has(field.id);
            const bgToken = hasClassifications
              ? isFieldError
                ? 'compliance.pdf.highlight.error'
                : isFieldWarning
                  ? 'compliance.pdf.highlight.warning'
                  : 'compliance.pdf.highlight.success'
              : 'compliance.pdf.highlight';
            const isActive =
              hasClassifications && activeFieldIdSet.has(field.id);
            // Per Figma: the active field gets a thicker solid border in
            // the corresponding status color and a low-opacity fill, so
            // the field reads as "the one you clicked" without obscuring
            // the underlying PDF text.
            const activeBorderColor = isFieldError
              ? 'red.500'
              : isFieldWarning
                ? 'yellow.600'
                : 'green.600';

            // Out of focus: another field is selected. It speaks only when
            // hovered or tapped, like a thinned-out field.
            const isOutOfFocus = hasFocus && !isActive;
            const message = messageForFieldId(field.id);
            const otherSelected = selectedOnPage.filter(
              (other) => other.id !== field.id
            );
            const calloutAnchor =
              isActive && message != null && otherSelected.length > 0
                ? selectionCalloutAnchor(
                    field,
                    message,
                    otherSelected,
                    { height: page.height, width: page.width },
                    calloutLayout
                  )
                : undefined;

            return (
              <FieldHighlight
                key={field.id}
                activeBorderColor={activeBorderColor}
                bgToken={bgToken}
                calloutAnchor={calloutAnchor}
                collisionMeasurementComplete={collisionMeasurementComplete}
                field={field}
                hideOverlappingFieldMessages={hideOverlappingFieldMessages}
                // Thinned out by 'firstPerPage'/'none': keep the highlight,
                // move the message behind a hover/tap.
                isActive={isActive}
                isCollisionHidden={
                  hideOverlappingFieldMessages &&
                  overlappingFieldIds.has(field.id)
                }
                isDefaultCaptioned={
                  (calloutFieldIds == null || calloutFieldIds.has(field.id)) &&
                  !isOutOfFocus
                }
                isDimmed={isOutOfFocus}
                isFieldError={isFieldError}
                isFieldWarning={isFieldWarning}
                message={message}
                page={page}
                registerFieldElement={registerFieldElement}
                scheduleOverlapMeasurement={scheduleOverlapMeasurement}
                splitSuggestionOnNewline={splitSuggestionOnNewline}
                wrapFieldMessages={wrapFieldMessages}
              />
            );
          })}
        </>
      );
    },
    [
      validationFieldsMap,
      selectedDocument?.fields,
      selectedContractDocumentId,
      isInternal,
      activeFieldId,
      activeFieldMessage,
      errorFieldMessages,
      warningFieldMessages,
      fieldMessageDisplay,
      hasClassifications,
      errorIdSet,
      successIdSet,
      warningIdSet,
      splitSuggestionOnNewline,
      wrapFieldMessages,
      focusSelectedFields,
      collisionMeasurementComplete,
      hideOverlappingFieldMessages,
      overlappingFieldIds,
      registerFieldElement,
      scheduleOverlapMeasurement,
    ]
  );

  if (selectedDocumentLoading) {
    return (
      <Box
        alignItems="center"
        display="flex"
        h="full"
        justifyContent="center"
        p={6}
      >
        <CenterSpinner />
      </Box>
    );
  } else if (!selectedDocument) {
    return (
      <Box
        alignItems="center"
        display="flex"
        h="full"
        justifyContent="center"
        p={6}
      >
        <Text>
          Select an ingestion document from the sidebar to view the PDF here.
        </Text>
      </Box>
    );
  }

  return (
    <Box
      ref={viewerRef}
      bg={containerBg}
      className="group"
      display="flex"
      flexDirection="column"
      height="100%"
      position="relative"
      px={paddingX}
      width="100%"
      onClickCapture={dismissPinnedRevealOnOutsideClick}
    >
      {showDocumentName && (
        <Box borderBottomWidth={1} flexShrink={0} p={2}>
          <Text fontSize="md" fontWeight="semibold" mb={1}>
            {selectedDocument.documentName}
          </Text>
        </Box>
      )}

      <Box flex={1} minHeight={0} width="100%">
        {/* Provider sits ABOVE PDFScrollable so a hover only re-renders the
            field highlights that consume it — `pageChildren` keeps its identity
            and the rects are never remounted from under the pointer. */}
        <RevealedFieldMessageContext.Provider value={revealedFieldMessage}>
          <PDFScrollable
            isDraggable
            defaultZoom={1}
            maxWidth={maxPageWidth ?? window.innerWidth}
            pageChildren={ValidationFieldPageChildren}
            pdfContainerProps={
              pagesPaddingY == null ? undefined : { py: pagesPaddingY }
            }
            pdfFiles={[
              { name: selectedDocument.documentName, url: pdfFileUrl },
            ]}
            showPageBorders={showPageBorders}
            onDocumentLoadError={(_pdfFile, error) => {
              setPageCounter({
                pdfUrl: pdfFileUrl,
                pageNumber: null,
                totalPages: null,
              });
              onPdfLoadError?.(error);
            }}
            onPagesMetadataChange={
              isInternal ? handlePagesMetadataChange : undefined
            }
            onVisiblePageChange={
              isInternal ? handleVisiblePageChange : undefined
            }
          />
        </RevealedFieldMessageContext.Provider>
      </Box>
      {showPageCounter && (
        <Box
          _groupHover={{ opacity: 1 }}
          backdropFilter="blur(6px)"
          bg="compliance.pdf.pageCounter.bg"
          borderRadius="full"
          bottom={4}
          color="compliance.pdf.pageCounter.text"
          fontSize="xs"
          opacity={0.7}
          pointerEvents="none"
          position="absolute"
          px={3}
          py={1}
          right={6}
          transition="opacity 150ms"
          zIndex={1}
        >
          Page {pageCounter.pageNumber} of {pageCounter.totalPages}
        </Box>
      )}
    </Box>
  );
};
